// Chalo mode on the client (docs/plans/2026-10-17-chalo.md): the police lines standing on the streets
// (docs/plans/2026-10-18-chokepoints.md), tolis as bodies, and the checklist HUD (plan section 5). Colours are the brand
// tokens: barricade stripes on night, sodium, khaki police.
import type { GameMap, Vec } from '../shared/map';
import type { DrawEnt, Standing } from './render';
import { ChaloData, ChaloGeo, ChaloSnap, CODE_GATE, LINE_KINDS, CalmKind } from '../shared/chalo';
import { HudName, MISSION_ICON, drawIcon, gateIcon } from './hud';
import { standing, turnFor } from './props';
import { FONT, INK, box, meter } from './ui';
import { NPC_R_M } from '../shared/sim';

// the HUD's inks on its asphalt dockets (src/client/ui.ts): newsprint type, metro-yellow highlights, police in blue
const BARRICADE = INK.metro, PAPER = INK.newsprint, KHAKI = INK.barricade, SODIUM = INK.metro, DIM = INK.newsprintDim;

/** Which way the bodies of a toli stand from its centre (a loose knot, the same for every toli): one in the middle, four round. */
const KNOT: [number, number][] = [[0, 0], [-0.9, 0.5], [0.9, 0.45], [-0.45, -0.75], [0.5, -0.7]].map(([x, y]) => {
  const d = Math.hypot(x, y);
  return d ? [x / d, y / d] : [0, 0];
});
/**
 * How far the four round a toli stand from the one in the middle, in metres: a body width plus the personal-space gap,
 * as the sim keeps between bodies (docs/plans/2026-10-19-fixes-round-1.md, A1). The four are at least 66 degrees apart
 * round the middle, so they stand no closer to each other than to it.
 */
export const knotRadiusM = (gapM: number) => 2 * NPC_R_M + gapM;
/** Body ids for a toli's people: far above any entity id (positive, as the animation clock needs), stable, so each keeps its character. */
export const bodyId = (toli: number, j: number) => 1_000_000 + toli * 8 + j;

/** How much further apart a toli's bodies stand once its hausla has gone entirely (1 = twice the knot's spread). */
const LOOSEN = 1.4;

/**
 * A toli's snapshot row [id, 3, x, y, face, n, hausla] as n crowd bodies. A steady toli stands as a tight knot and a
 * fraying one loosens, so a crowd losing its nerve reads as one on the ground before any meter says so
 * (docs/plans/2026-10-18-crowd-as-structure.md). `nerve` is 0..100 off the wire; rows without it are steady. `gapM`:
 * the stages file's personal space, so a steady knot's bodies stand as far apart as any two in the crowd.
 */
export function toliBodies(id: number, x: number, y: number, face: number, n: number, metre: number, nerve = 100, gapM = 0): DrawEnt[] {
  const out: DrawEnt[] = [];
  const spread = (1 + (1 - Math.max(0, Math.min(100, nerve)) / 100) * LOOSEN) * knotRadiusM(gapM);
  for (let j = 0; j < Math.min(n, KNOT.length); j++) {
    out.push({ id: bodyId(id, j), type: 2, x: x + KNOT[j][0] * metre * spread, y: y + KNOT[j][1] * metre * spread, face });
  }
  return out;
}

/**
 * The gaps on the ground, under the barricades standing in them: a dark pad while shut, a pale one while open, khaki
 * while police walk back. The cordon itself is the barricades (CordonProps), not paint.
 */
export function drawCordon(g: CanvasRenderingContext2D, m: GameMap, G: ChaloGeo, ch: ChaloSnap | null, vx0: number, vy0: number, vw: number, vh: number) {
  const t = m.tile, x0 = Math.max(0, Math.floor(vx0 / t)), y0 = Math.max(0, Math.floor(vy0 / t));
  const x1 = Math.min(m.w - 1, Math.floor((vx0 + vw) / t)), y1 = Math.min(m.h - 1, Math.floor((vy0 + vh) / t));
  g.save();
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) {
    const gap = G.gapOf[ty * m.w + tx] - 1;
    if (gap < 0) continue;
    const px = tx * t, py = ty * t, st = ch ? CODE_GATE[ch.cs[gap]?.[0] ?? 0] : 'closed';
    if (st === 'closed' || st === 'game') { g.fillStyle = 'rgba(18,22,32,.45)'; g.fillRect(px, py, t, t); continue; } // a game is played against a shut gate
    g.fillStyle = st === 'back' ? 'rgba(166,139,91,.32)' : 'rgba(241,236,224,.16)';
    g.fillRect(px, py, t, t);
    g.strokeStyle = st === 'back' ? KHAKI : PAPER; g.lineWidth = t / 30; g.setLineDash([t / 10, t / 12]);
    g.strokeRect(px + t * 0.06, py + t * 0.06, t * 0.88, t * 0.88); g.setLineDash([]);
  }
  g.restore();
}

/** One checkpoint in a ring's line: its gate icon, its text as in the line's body, and its game while shut. */
export interface CheckItem { icon: HudName; text: string; mission?: HudName; game?: string }
/** One checklist line, as drawn and as read by the browser check (head and body); items and icons are for drawing. */
export interface CheckLine { head: string; body: string; done?: boolean; items?: CheckItem[]; icons?: HudName[] }

// ------------------------------------------------------------------ the police lines, standing

const FENCE = 'oga-concert-props/barricade', NAKA = 'kenney-city-kit-roads/construction-barrier', CONE = 'kenney-city-kit-roads/construction-cone';
/**
 * Each barricade model's length along its row (metres, life size: the obj's extent times assets/models/scale.json) and
 * the turn of its sprite that lies along the screen's x: the jersey block's @0 runs up the screen.
 */
const UNIT: Record<string, { m: number; turn: number }> = { [FENCE]: { m: 2.37, turn: 0 }, [NAKA]: { m: 1.69, turn: 90 } };
/** Body tier (props.ts): barricades keep life ratios with the bodies, so a 1.1 m fence comes up to a hip. */
const BODY_TIER = 4.0;
const keyAt = (model: string, deg: number) => `${model}@${(turnFor(deg) + UNIT[model].turn) % 180}`;
const rad = (deg: number) => (deg * Math.PI) / 180;

interface Piece { key: string; x: number; y: number; tier?: 'body' | 'structure' }

/**
 * The cordon's rows as props that stand and sort with the bodies (docs/plans/2026-10-18-chokepoints.md): steel crowd
 * fence on the pavements, concrete blocks across the carriageway, and in front of each gap a funnel of cones where the
 * tolis queue. The police lines in the gaps, the booths and the CCTV poles are the Chalo world's (chaloworld.ts).
 */
export class CordonProps {
  private fixed: Piece[] = [];

  constructor(private m: GameMap, G: ChaloGeo, D: ChaloData) {
    const t = m.tile, metre = t / (m.mpt || 1), unit = (model: string) => UNIT[model].m * BODY_TIER * metre;
    const mid = (p: number[]): Vec => ({ x: (p[0] + 0.5) * t, y: (p[1] + 0.5) * t });
    for (const ring of D.rings.rings) {
      for (const run of ring.runs) {
        const model = run.kind === 'naka' ? NAKA : FENCE, pts = run.tiles.map(mid);
        // the row from kerb to kerb: half a tile past the first and last tile centres, along the line
        const a0 = rad(run.along), ux = Math.cos(a0), uy = Math.sin(a0);
        const len = (pts.length - 1) * t + t, u = Math.max(1, Math.round(len / unit(model)));
        for (let i = 0; i < u; i++) {
          const s = ((i + 0.5) * len) / u - t / 2, p = alongPts(pts, s, ux, uy);
          const d = pts.length >= 3 ? dirAt(pts, s, t) : run.along;
          this.fixed.push({ key: keyAt(model, d), x: p.x, y: p.y });
        }
      }
    }
    G.cps.forEach((_, k) => {
      const J = D.rings.checkpoints[k], a = { x: Math.cos(rad(J.along)), y: Math.sin(rad(J.along)) };
      const c = { x: J.x * t, y: J.y * t }, w = J.gapTiles.length * t;
      let n = { x: -a.y, y: a.x }; // across the line, pointing in (towards the stage)
      if (n.x * (G.centre.x - c.x) + n.y * (G.centre.y - c.y) < 0) n = { x: -n.x, y: -n.y };
      // the funnel: three cones a side, widening away from the gap, where the queue stands
      for (let j = 1; j <= 3; j++) for (const sd of [-1, 1]) {
        const out = j * 0.8 * t, half = w / 2 + j * 0.35 * t;
        this.fixed.push({ key: CONE, x: c.x - n.x * out + a.x * half * sd, y: c.y - n.y * out + a.y * half * sd });
      }
    });
  }

  /** The rows and funnels near the view, standing. */
  standing(vx0: number, vy0: number, vw: number, vh: number): Standing[] {
    const metre = this.m.tile / (this.m.mpt || 1), pad = 3 * this.m.tile, out: Standing[] = [];
    for (const p of this.fixed) {
      if (p.x < vx0 - pad || p.x > vx0 + vw + pad || p.y < vy0 - pad || p.y > vy0 + vh + pad) continue;
      const s = standing(p.key, p.x, p.y, metre, p.tier);
      if (s) out.push(s);
    }
    return out;
  }
}

/** The point `s` px along the polyline `pts`, carried on straight past either end along (ux, uy). */
function alongPts(pts: Vec[], s: number, ux: number, uy: number): Vec {
  if (s <= 0 || pts.length === 1) return { x: pts[0].x + ux * s, y: pts[0].y + uy * s };
  for (let i = 0; i + 1 < pts.length; i++) {
    const d = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (s <= d) return { x: pts[i].x + ((pts[i + 1].x - pts[i].x) * s) / d, y: pts[i].y + ((pts[i + 1].y - pts[i].y) * s) / d };
    s -= d;
  }
  const e = pts[pts.length - 1];
  return { x: e.x + ux * s, y: e.y + uy * s };
}

/** The row's direction at `s`, over a tile and a half either way, so a staircase of tiles reads as one diagonal (degrees). */
function dirAt(pts: Vec[], s: number, t: number): number {
  const a = alongPts(pts, Math.max(0, s - 1.5 * t), 0, 0), b = alongPts(pts, s + 1.5 * t, 0, 0);
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}


const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;
const SUPPORT_ICON: Record<string, HudName> = { water: 'water', banner: 'flag', film: 'video' };

/**
 * The checklist (plan section 5): the goal, a line per ring with each checkpoint's state, then the support tasks.
 * `from` (your position, world px; `metre` world px per metre) adds how far each unopened checkpoint of the ring in
 * front is: pass null while the blackout hides distances. `dark` is the blackout's seconds left (0: none).
 */
export function checklist(D: ChaloData, G: ChaloGeo, ch: ChaloSnap, timeLeft: number, from: Vec | null = null, metre = 1, dark = 0): CheckLine[] {
  const [site, goal] = ch.c, bar = Math.round(Math.min(1, goal ? site / goal : 0) * 10);
  const lines: CheckLine[] = [{ head: 'JANTAR MANTAR', body: `${'█'.repeat(bar)}${'░'.repeat(10 - bar)}  ${site} / ${goal}   ${mmss(timeLeft)}`, done: site >= goal }];
  const heads = ['OUTER LINE', 'MIDDLE LINE', 'INNER GATES'];
  G.ringNames.forEach((_, r) => {
    const items = G.cps.map((cp, k) => ({ cp, k })).filter(({ cp }) => cp.ring === r).map(({ cp, k }): CheckItem => {
      const [code, left, opened] = ch.cs[k] ?? [0, 0, 0], st = CODE_GATE[code];
      // outer and middle: ✓ once opened, ◐ while a game runs; inner gates: ● while open
      const inner = r === G.ringNames.length - 1;
      const mark = st === 'game' ? '◐' : inner ? (st === 'open' || st === 'back' ? '●' : '○') : opened ? '✓' : '·';
      const sit = ch.dh?.find((d) => d[0] === k), talk = ch.tk?.find((t) => t[0] === k);
      const sitTalk = sit ? ` sit-in ${Math.floor(sit[4] / 10)}/${sit[6] / 10} s, calm ${sit[2] / 10} of ${sit[3] / 10}`
        : talk ? ` talking, mood ${talk[3]}` : '';
      const game = !sit && !talk && st === 'closed' ? D.rules.missions[cp.mission]?.name.en ?? cp.mission : undefined;
      const extra = sitTalk || (st === 'open' && left > 0 ? ` open ${mmss(left)}` : st === 'back' ? ' closing' : game ? ` · ${game}` : '');
      const far = from && r === ch.r && st !== 'open' ? ` ${Math.round(Math.hypot(cp.at.x - from.x, cp.at.y - from.y) / metre)} m` : '';
      return { icon: gateIcon(code, left), text: `${mark} ${cp.name}${extra}${far}`, mission: game ? MISSION_ICON[cp.mission] : undefined, game };
    });
    const fallen = (ch.f >> r) & 1;
    lines.push({ head: heads[r] ?? G.ringNames[r].toUpperCase(), body: items.map((i) => i.text).join('   ') + (fallen ? '   (fallen)' : ''), done: !!fallen, items });
  });
  // the bonus line (plan section 5): nobody detained so far, and a ring's checkpoint opened by talking
  const held = ch.c[4] ?? 0, talked = G.cps.some((cp, k) => cp.ring < G.ringNames.length - 1 && ch.cs[k]?.[4] === 1);
  lines.push({ head: 'BONUS', body: `${held ? `✗ ${held} detained` : '✓ Nobody detained'}   ${talked ? '✓' : '·'} Opened by talk`, done: !held && talked });
  // support: water, the banner, filming the van (their progress arrives with the support tasks)
  const support = D.rules.checklist.filter((c) => c.kind === 'support');
  if (support.length) lines.push({ head: 'SUPPORT', body: support.map((c) => c.text.en).join('   '), icons: support.map((c) => SUPPORT_ICON[c.id] ?? 'flag') });
  if (dark > 0) lines.push({ head: 'BLACKOUT', body: `no map, no distances for ${mmss(Math.ceil(dark))}` });
  return lines;
}

const FONT_HEAD = FONT.mono(12, 600), FONT_BODY = FONT.mono(12, 500);
const ICON = 13, ICON_GAP = 4, LINE_H = 20;
type Run = { icon: HudName; colour: string } | { text: string; colour: string };

/** A line as runs of icons and text: a gate icon before each checkpoint, its game's icon before the game's name. */
function runsOf(l: CheckLine, i: number): Run[] {
  const tc = i === 0 || l.head === 'BLACKOUT' ? SODIUM : PAPER;
  if (l.items) {
    const out: Run[] = [];
    l.items.forEach((it, j) => {
      if (j) out.push({ text: '   ', colour: tc });
      out.push({ icon: it.icon, colour: it.icon === 'warning' ? SODIUM : it.icon === 'unlocked' ? PAPER : DIM });
      const cut = it.game ? it.text.indexOf(` · ${it.game}`) : -1;
      if (it.mission && cut >= 0) out.push({ text: it.text.slice(0, cut) + '  ', colour: tc }, { icon: it.mission, colour: BARRICADE }, { text: it.text.slice(cut + 3), colour: DIM });
      else out.push({ text: it.text, colour: tc });
    });
    if (l.body.endsWith('(fallen)')) out.push({ text: '   (fallen)', colour: SODIUM });
    return out;
  }
  if (l.icons?.length) return l.body.split('   ').flatMap((p, j): Run[] => [...(j ? [{ text: '   ', colour: tc }] : []), ...(l.icons![j] ? [{ icon: l.icons![j], colour: PAPER }] : []), { text: p, colour: DIM }]);
  return [{ text: l.body, colour: tc }];
}

export function drawChecklist(g: CanvasRenderingContext2D, lines: CheckLine[], x: number, y: number) {
  g.save();
  g.textAlign = 'left'; g.textBaseline = 'alphabetic'; g.font = FONT_BODY;
  const runs = lines.map(runsOf), bx = x + 118;
  const width = (rs: Run[]) => rs.reduce((w, r) => w + ('icon' in r ? ICON + ICON_GAP : g.measureText(r.text).width), 0);
  const w = Math.max(520, ...runs.map((rs) => 118 + width(rs) + 20)), h = lines.length * LINE_H + 12;
  box(g, x - 10, y - 17, w, h, INK.asphalt, 0, 4); // a solid docket: no see-through panels
  g.fillStyle = INK.metro; g.fillRect(x - 10, y - 17, 4, h);
  lines.forEach((l, i) => {
    const ly = y + i * LINE_H;
    g.font = FONT_HEAD; g.fillStyle = l.done ? SODIUM : DIM;
    g.fillText(l.head, x, ly);
    g.font = FONT_BODY;
    let cx = bx;
    for (const r of runs[i]) {
      if ('icon' in r) { drawIcon(g, r.icon, cx, ly - 11, ICON, r.colour); cx += ICON + ICON_GAP; continue; }
      g.fillStyle = r.colour; g.fillText(r.text, cx, ly); cx += g.measureText(r.text).width;
    }
  });
  g.restore();
}

// ------------------------------------------------------------------ build step 2: Dharna, Baat, detention

/**
 * What E does here, for the prompt under the clock: sit in at an inner gate, talk at the inspector's gate. Mirrors the
 * sim's reach (the sim decides; this only says what it will try). `sitting`: the gate sat in at, or -1.
 */
export function usePrompt(D: ChaloData, G: ChaloGeo, ch: ChaloSnap, x: number, y: number, metre: number, level: number, sitting: number, talking: boolean): string | null {
  if (sitting >= 0) return 'Sitting in. E or walk to stand up';
  if (talking) return null;
  const M = D.rules.missions;
  let best: string | null = null, bd = Infinity;
  G.cps.forEach((cp, k) => {
    const [code, , , cool] = ch.cs[k] ?? [0, 0, 0, 0], st = CODE_GATE[code], d = Math.hypot(cp.at.x - x, cp.at.y - y);
    if (level !== cp.ring || d >= bd) return;
    if (cp.mission === 'dharna' && d <= M.dharna.play.sitWithinM * metre && st !== 'back') { best = `E · Sit in at the ${cp.name} (${M.dharna.name.en})`; bd = d; }
    if (cp.mission === 'baat' && d <= M.baat.play.talkWithinM * metre && st === 'closed') { best = cool > 0 ? `The inspector will talk again in ${cool} s` : `E · Talk to the inspector (${M.baat.name.en})`; bd = d; }
  });
  return best;
}

/** A mat under each player sitting in (rows of type 0 whose state field is 1), sized to the body. World coordinates. */
export function drawMats(g: CanvasRenderingContext2D, pts: { x: number; y: number }[], bodyR: number) {
  g.save(); g.fillStyle = 'rgba(248,196,10,.4)'; g.strokeStyle = SODIUM; g.lineWidth = bodyR * 0.14;
  for (const p of pts) { g.beginPath(); g.ellipse(p.x, p.y, bodyR * 2.6, bodyR * 1.5, 0, 0, Math.PI * 2); g.fill(); g.stroke(); }
  g.restore();
}

const KIND_LABEL: Record<string, string> = { facts: 'Facts', rights: 'Rights', people: 'People', heat: 'Heat' };

/** Baat's panel for the talker (lower middle of the screen): the inspector's words, mood, the time to pick, three lines. Screen px. */
export function drawTalk(g: CanvasRenderingContext2D, D: ChaloData, row: number[], W: number, H: number) {
  const SC = D.rules.missions.baat.script, P = D.rules.missions.baat.play;
  const [, , ex, mood, left, hk, hi, ...rest] = row, last = rest[6] ?? 0;
  const hint = SC.hints[LINE_KINDS[hk] as CalmKind]?.[hi]?.en ?? '';
  const w = Math.min(560, W - 32), h = 196, x = (W - w) / 2, y = H - h - 92;
  g.save();
  box(g, x, y, w, h, INK.newsprint, 3, 6); // the transcript docket: newsprint, ink border, hard shadow
  g.fillStyle = INK.asphalt; g.fillRect(x, y, w, 34);
  g.textAlign = 'left'; g.textBaseline = 'alphabetic'; g.fillStyle = INK.newsprint; g.font = FONT.mono(12, 600);
  g.fillText(`TALK · ${Math.min(ex + 1, P.exchanges)} OF ${P.exchanges} · ${(left / 10).toFixed(0)} S`, x + 14, y + 22);
  // mood: a meter to the win line at moodWin
  const bx = x + w - 214, bw = 200;
  meter(g, bx, y + 11, bw, 12, Math.min(100, mood) / 100, mood >= P.moodWin ? INK.metro : INK.newsprint);
  g.fillStyle = INK.riso; g.fillRect(bx + (bw * P.moodWin) / 100 - 1, y + 7, 3, 20);
  g.fillStyle = INK.newsprintDim; g.textAlign = 'right'; g.fillText(`MOOD ${mood}${ex > 0 ? ` (${last > 0 ? '+' : ''}${last})` : ''}`, bx - 10, y + 22);
  g.textAlign = 'left'; g.fillStyle = INK.asphalt; g.font = FONT.body(16, 700);
  g.fillText(`Inspector: “${hint}”`, x + 14, y + 58, w - 28);
  for (let i = 0; i < 3; i++) {
    const kd = LINE_KINDS[rest[i * 2]], line = SC.lines[kd]?.[rest[i * 2 + 1]]?.en ?? '';
    const ly = y + 92 + i * 33;
    g.fillStyle = INK.newsprint2; g.fillRect(x + 12, ly - 20, w - 24, 28);
    g.fillStyle = INK.asphalt; g.fillRect(x + 12, ly - 20, 26, 28);
    g.fillStyle = INK.metro; g.font = FONT.display(20); g.fillText(`${i + 1}`, x + 20, ly + 1);
    g.fillStyle = INK.inkDim; g.font = FONT.mono(11, 600); g.fillText(KIND_LABEL[kd].toUpperCase(), x + 46, ly);
    g.fillStyle = INK.asphalt; g.font = FONT.body(14, 500); g.fillText(line, x + 106, ly, w - 122);
  }
  g.restore();
}

/** The debrief's icon: the trophy for a win, a medal for turning up. */
export const resultIcon = (ch: ChaloSnap): HudName => (ch.w === 1 ? 'trophy' : 'medal1');

/** The round's end line. */
export function resultLine(ch: ChaloSnap): [string, string] {
  const [site, goal, , left] = ch.c;
  return ch.w === 1
    ? ['CHALO! THE MARCH STARTS', `${site} people at Jantar Mantar`]
    : ['NOT ENOUGH, THIS TIME', `${site} of ${goal} at Jantar Mantar · ${left} went home waiting`];
}
